<template>
  <div class="token-widget">
    <!-- Tabs (if multiple) -->
    <div v-if="tabs.length > 1" class="token-widget__tabs">
      <button
        v-for="tab in tabs"
        :key="tab.key"
        class="token-widget__tab"
        :class="{ 'token-widget__tab--active': activeTab === tab.key }"
        @click="switchTab(tab.key)"
      >{{ tab.label }}</button>
    </div>

    <!-- Content -->
    <div v-if="mounted" class="token-widget__body" :class="{ 'token-widget__loading': loading }">
      <!-- Compact list variant -->
      <template v-if="variant === 'compact'">
        <div class="token-list-compact">
          <a v-for="token in displayTokens" :key="token.mint"
             :href="'/tokens/' + token.mint"
             class="token-list-compact__item">
            <div class="d-flex align-items-center gap-2 flex-grow-1 min-w-0">
              <img v-if="token.logoURI" :src="token.logoURI" class="token-list-compact__logo" loading="lazy">
              <div v-else class="token-list-compact__logo-placeholder">{{ (token.symbol||'?')[0] }}</div>
              <span class="token-list-compact__symbol">{{ token.symbol }}</span>
              <span v-if="showVolume" class="token-list-compact__meta">{{ shortNumber(token.volume_usd_24h, '$') }}</span>
            </div>
            <div class="d-flex align-items-center gap-2">
              <span class="token-list-compact__price mono-num">${{ formatPrice(token.price_usd) }}</span>
              <span class="token-list-compact__change" :class="token.change_24h_percent >= 0 ? 'text-success' : 'text-danger'">
                {{ formatChange(token.change_24h_percent) }}
              </span>
            </div>
          </a>
        </div>
      </template>

      <!-- Table variant -->
      <template v-else-if="variant === 'table'">
        <div class="table-responsive">
          <table class="table mb-0 token-list-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Token</th>
                <th class="text-end">Price</th>
                <th class="text-end">24h</th>
                <th v-if="showVolume" class="text-end">Vol</th>
                <th v-if="showMcap" class="text-end">MCap</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(token, i) in displayTokens" :key="token.mint"
                  class="token-list-table__row" @click="$event.preventDefault(); window.location='/tokens/'+token.mint">
                <td class="text-muted">{{ i + 1 }}</td>
                <td>
                  <a :href="'/tokens/' + token.mint" class="d-flex align-items-center gap-2 text-decoration-none">
                    <img v-if="token.logoURI" :src="token.logoURI" class="token-list-table__logo" loading="lazy">
                    <div v-else class="token-list-table__logo-placeholder">{{ (token.symbol||'?')[0] }}</div>
                    <div>
                      <div class="token-list-table__symbol">{{ token.symbol }}</div>
                      <div class="token-list-table__name">{{ truncate(token.name, 14) }}</div>
                    </div>
                  </a>
                </td>
                <td class="text-end mono-num">${{ formatPrice(token.price_usd) }}</td>
                <td class="text-end" :class="token.change_24h_percent >= 0 ? 'text-success' : 'text-danger'">
                  {{ formatChange(token.change_24h_percent) }}
                </td>
                <td v-if="showVolume" class="text-end">{{ shortNumber(token.volume_usd_24h, '$') }}</td>
                <td v-if="showMcap" class="text-end">
                  <span v-if="token.market_cap_usd > 0">{{ shortNumber(token.market_cap_usd, '$') }}</span>
                  <span v-else class="text-muted">-</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>

      <!-- Pills variant -->
      <template v-else-if="variant === 'pills'">
        <div class="token-pills">
          <a v-for="token in displayTokens" :key="token.mint"
             :href="'/tokens/' + token.mint"
             class="token-pills__item">
            <img v-if="token.logoURI" :src="token.logoURI" class="token-pills__logo" loading="lazy">
            <span class="token-pills__symbol">{{ token.symbol }}</span>
            <span class="token-pills__price mono-num">${{ formatPrice(token.price_usd) }}</span>
            <span class="token-pills__change" :class="token.change_24h_percent >= 0 ? 'text-success' : 'text-danger'">
              {{ formatChange(token.change_24h_percent) }}
            </span>
          </a>
        </div>
      </template>

      <!-- Featured variant -->
      <template v-else-if="variant === 'featured'">
        <div class="row g-2">
          <div v-for="token in displayTokens" :key="token.mint" class="col-12 col-md-6">
            <a :href="'/tokens/' + token.mint" class="token-featured-card text-decoration-none">
              <div class="d-flex align-items-start gap-3">
                <div>
                  <img v-if="token.logoURI" :src="token.logoURI" class="token-featured-card__logo" loading="lazy">
                  <div v-else class="token-featured-card__logo-placeholder">{{ (token.symbol||'?')[0] }}</div>
                </div>
                <div class="flex-grow-1">
                  <div class="d-flex align-items-center gap-2 mb-1">
                    <span class="token-featured-card__symbol">{{ token.symbol }}</span>
                    <span class="token-featured-card__name">{{ truncate(token.name, 18) }}</span>
                  </div>
                  <div class="d-flex align-items-baseline gap-2">
                    <span class="token-featured-card__price mono-num">${{ formatPrice(token.price_usd) }}</span>
                    <span class="token-featured-card__change" :class="token.change_24h_percent >= 0 ? 'text-success' : 'text-danger'">
                      {{ formatChange(token.change_24h_percent) }}
                    </span>
                  </div>
                  <div class="token-featured-card__meta mt-1">
                    <span v-if="token.market_cap_usd > 0">MCap {{ shortNumber(token.market_cap_usd, '$') }}</span>
                    <span>Vol {{ shortNumber(token.volume_usd_24h, '$') }}</span>
                  </div>
                </div>
              </div>
              <div class="mt-2">
                <sparkline-chart :data="(sparklines[token.mint]?.prices || sparklines[token.mint] || [])" :width="260" :height="36" />
              </div>
            </a>
          </div>
        </div>
      </template>

      <!-- Grid variant -->
      <template v-else-if="variant === 'grid'">
        <div class="token-grid" :style="{ '--grid-cols': cols }">
          <a v-for="token in displayTokens" :key="token.mint"
             :href="'/tokens/' + token.mint"
             class="token-grid__item">
            <img v-if="token.logoURI" :src="token.logoURI" class="token-grid__logo" loading="lazy">
            <div v-else class="token-grid__logo-placeholder">{{ (token.symbol||'?')[0] }}</div>
            <div class="token-grid__info">
              <div class="token-grid__symbol">{{ token.symbol }}</div>
              <div class="token-grid__price mono-num">${{ formatPrice(token.price_usd) }}</div>
            </div>
            <span class="token-grid__change" :class="token.change_24h_percent >= 0 ? 'text-success' : 'text-danger'">
              {{ formatChange(token.change_24h_percent) }}
            </span>
          </a>
        </div>
      </template>

      <!-- Hero variant (spotlight + sidebar integrated) -->
      <template v-else-if="variant === 'hero' && heroToken">
        <div class="token-hero-card">
          <div class="token-hero-card__spotlight">
            <component :is="chooser ? 'div' : 'a'" :href="chooser ? undefined : '/tokens/' + heroToken.mint" class="token-hero-card__header">
              <img v-if="heroToken.logoURI" :src="heroToken.logoURI" class="token-hero-card__logo" loading="lazy">
              <div v-else class="token-hero-card__logo-placeholder">{{ (heroToken.symbol||'?')[0] }}</div>
              <div class="token-hero-card__identity">
                <span class="token-hero-card__symbol">{{ heroToken.symbol }}</span>
                <span class="token-hero-card__name">{{ truncate(heroToken.name, 20) }}</span>
              </div>
              <div class="token-hero-card__price-block">
                <span class="token-hero-card__price mono-num">${{ formatPrice(heroToken.price_usd) }}</span>
                <span class="token-hero-card__change" :class="heroToken.change_24h_percent >= 0 ? 'text-success' : 'text-danger'">
                  {{ formatChange(heroToken.change_24h_percent) }}
                </span>
              </div>
            </component>
            <div class="token-hero-card__chart">
              <token-price-chart :data="(sparklines[heroToken.mint]?.prices || sparklines[heroToken.mint] || [])" :height="100" />
            </div>
            <div class="token-hero-card__stats">
              <span v-if="heroToken.market_cap_usd > 0" class="token-hero-card__stat">MCap <strong>{{ shortNumber(heroToken.market_cap_usd, '$') }}</strong></span>
              <span class="token-hero-card__stat">Vol <strong>{{ shortNumber(heroToken.volume_usd_24h, '$') }}</strong></span>
              <span class="flex-grow-1"></span>
              <a :href="'/tokens/' + heroToken.mint" class="token-hero-card__detail-link">
                View {{ heroToken.symbol }} &rarr;
              </a>
            </div>
          </div>
          <div v-if="heroSidebarTokens.length > 0" class="token-hero-card__sidebar">
            <component
              v-for="(token, i) in heroSidebarTokens" :key="token.mint"
              :is="chooser ? 'button' : 'a'"
              :href="chooser ? undefined : '/tokens/' + token.mint"
              :class="['token-hero-card__sidebar-item', { 'token-hero-card__sidebar-item--active': chooser && token.mint === heroToken.mint }]"
              @click="chooser ? selectHero(displayTokens.indexOf(token)) : undefined"
            >
              <img v-if="token.logoURI" :src="token.logoURI" class="token-hero-card__sidebar-logo" loading="lazy">
              <div v-else class="token-hero-card__sidebar-logo-placeholder">{{ (token.symbol||'?')[0] }}</div>
              <span class="token-hero-card__sidebar-symbol">{{ token.symbol }}</span>
              <span class="flex-grow-1"></span>
              <span class="token-hero-card__sidebar-price mono-num">${{ formatPrice(token.price_usd) }}</span>
              <span class="token-hero-card__sidebar-change" :class="token.change_24h_percent >= 0 ? 'text-success' : 'text-danger'">
                {{ formatChange(token.change_24h_percent) }}
              </span>
            </component>
          </div>
        </div>
      </template>

      <!-- Marquee variant (scrolling ticker) -->
      <template v-else-if="variant === 'marquee'">
        <div class="token-marquee" :style="{ '--marquee-speed': marqueeSpeed + 's' }">
          <div class="token-marquee__track">
            <div class="token-marquee__content" v-for="copy in 2" :key="copy">
              <a v-for="token in displayTokens" :key="token.mint + '-' + copy"
                 :href="'/tokens/' + token.mint" class="token-marquee__item">
                <img v-if="token.logoURI" :src="token.logoURI" class="token-marquee__logo" loading="lazy">
                <span class="token-marquee__symbol">{{ token.symbol }}</span>
                <span class="token-marquee__price mono-num">${{ formatPrice(token.price_usd) }}</span>
                <span class="token-marquee__change" :class="token.change_24h_percent >= 0 ? 'text-success' : 'text-danger'">
                  {{ formatChange(token.change_24h_percent) }}
                </span>
              </a>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- SSR fallback (server-rendered content, hidden once Vue mounts) -->
    <div v-else>
      <slot name="ssr-fallback"></slot>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import axios from 'axios';

const props = defineProps({
  // Initial SSR data (tokens array)
  initialTokens: { type: Array, default: () => [] },
  // Display variant: compact | table | pills | featured
  variant: { type: String, default: 'compact' },
  // Tabs config: [{ key: 'stocks', label: 'Stocks', category: 'stocks' }, ...]
  tabs: { type: Array, default: () => [] },
  // Default data source (if no tabs)
  category: { type: String, default: '' },
  filterGroup: { type: String, default: 'top' },
  limit: { type: Number, default: 10 },
  // Column toggles
  showVolume: { type: Boolean, default: false },
  showMcap: { type: Boolean, default: true },
  // Grid columns (for grid variant)
  cols: { type: Number, default: 3 },
  // Marquee scroll speed in seconds (for marquee variant)
  marqueeSpeed: { type: Number, default: 30 },
  // Hero: clicking sidebar tokens switches the spotlight (instead of navigating)
  chooser: { type: Boolean, default: false },
  // Refresh interval in ms (0 = no refresh)
  refreshInterval: { type: Number, default: 30000 },
});

const mounted = ref(false);
const loading = ref(false);
const tokens = ref(props.initialTokens || []);
const heroIndex = ref(0); // which token is in the spotlight
const sparklines = ref({});
const activeTab = ref(props.tabs[0]?.key || '');
let refreshTimer = null;

const displayTokens = computed(() => tokens.value.slice(0, props.limit));

// Hero variant: which token is spotlighted and which are in sidebar
const heroToken = computed(() => displayTokens.value[heroIndex.value] || displayTokens.value[0]);
const heroSidebarTokens = computed(() => {
  if (props.chooser) {
    // Chooser mode: ALL tokens in sidebar (including hero, so user can switch back)
    return displayTokens.value;
  }
  // Default: hero excluded from sidebar
  return displayTokens.value.filter((_, i) => i !== 0);
});

const selectHero = (index) => {
  heroIndex.value = index;
  // Fetch sparkline for the newly selected token if not already cached
  const mint = displayTokens.value[index]?.mint;
  if (mint && !sparklines.value[mint]) {
    axios.get('/api/tokens/sparklines', { params: { mints: mint } })
      .then(({ data }) => { sparklines.value = { ...sparklines.value, ...data }; })
      .catch(() => {});
  }
};

// Formatters
const formatPrice = (p) => {
  if (!p) return '0.00';
  if (p >= 1) return p.toFixed(2);
  if (p >= 0.01) return p.toFixed(4);
  return p.toPrecision(3);
};
const shortNumber = (v, prefix = '') => {
  if (!v) return prefix + '0';
  const a = Math.abs(v);
  if (a >= 1e9) return (v < 0 ? '-' : '') + prefix + (a / 1e9).toFixed(1) + 'B';
  if (a >= 1e6) return (v < 0 ? '-' : '') + prefix + (a / 1e6).toFixed(1) + 'M';
  if (a >= 1e3) return (v < 0 ? '-' : '') + prefix + (a / 1e3).toFixed(1) + 'K';
  return (v < 0 ? '-' : '') + prefix + Math.round(a);
};
const formatChange = (v) => (v >= 0 ? '+' : '') + (v || 0).toFixed(1) + '%';
const truncate = (s, l) => s && s.length > l ? s.slice(0, l) + '...' : (s || '');

// Data fetching
const fetchTokens = async () => {
  loading.value = true;
  const params = { limit: props.limit, sort: 'volume' };

  // Determine data source from active tab or props
  const tab = props.tabs.find(t => t.key === activeTab.value);
  if (tab?.category) params.category = tab.category;
  else if (tab?.filterGroup) params.filter_group = tab.filterGroup;
  else if (props.category) params.category = props.category;
  else params.filter_group = props.filterGroup;

  try {
    const { data } = await axios.get('/api/tokens/widget', { params });
    tokens.value = data.tokens || [];

    // Fetch sparklines for variants that show charts
    if (['featured', 'hero'].includes(props.variant) && tokens.value.length) {
      const mints = tokens.value.map(t => t.mint).join(',');
      axios.get('/api/tokens/sparklines', { params: { mints } })
        .then(({ data }) => { sparklines.value = data || {}; })
        .catch(() => {});
    }
  } catch (e) {
    console.error('TokenWidget fetch error:', e);
  } finally {
    loading.value = false;
  }
};

const switchTab = (key) => {
  activeTab.value = key;
  fetchTokens();
};

// Price refresh
const refreshPrices = async () => {
  const mints = tokens.value.map(t => t.mint).join(',');
  if (!mints) return;
  try {
    const { data } = await axios.get('/api/tokens/prices', { params: { mints } });
    tokens.value = tokens.value.map(t => {
      const u = data[t.mint];
      return u?.price_usd !== undefined ? { ...t, price_usd: u.price_usd } : t;
    });
  } catch (e) {}
};

onMounted(() => {
  mounted.value = true;
  if (!tokens.value.length) {
    fetchTokens();
  } else if (['featured', 'hero'].includes(props.variant)) {
    // Initial tokens exist from SSR but sparklines need async fetch
    const mints = tokens.value.map(t => t.mint).join(',');
    axios.get('/api/tokens/sparklines', { params: { mints } })
      .then(({ data }) => { sparklines.value = data || {}; })
      .catch(() => {});
  }
  if (props.refreshInterval > 0) {
    refreshTimer = setInterval(refreshPrices, props.refreshInterval);
  }
});
onUnmounted(() => { if (refreshTimer) clearInterval(refreshTimer); });
</script>

